import React, { useEffect, useMemo, useState } from 'react';
import { getCanonicalEmail, getDisplayName } from '../utils/userProfiles';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const DEFAULT_REVIEW_ROUNDS = [
  { roundNumber: 1, title: 'Review 1', description: 'ETA Internal Reviewers' },
  { roundNumber: 2, title: 'Review 2', description: '' },
];

const ReviewRounds = () => {
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [programRounds, setProgramRounds] = useState([]);
  const [users, setUsers] = useState([]);
  const [roundAssignments, setRoundAssignments] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) return;

    const fetchPrograms = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/programs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setPrograms(data);
      } catch (err) {
        console.error('Failed to load programs:', err);
      }
    };

    fetchPrograms();
  }, [token]);

  const selectedProgram = useMemo(
    () => programs.find((program) => String(program.id || program._id) === String(selectedProgramId)) || null,
    [programs, selectedProgramId]
  );

  useEffect(() => {
    if (!token || !selectedProgramId) {
      setProgramRounds([]);
      return;
    }

    const fetchProgramRounds = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/programs/${selectedProgramId}/review-rounds`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to load review rounds (${res.status})`);
        const data = await res.json();
        setProgramRounds((data.reviewRoundTemplates || []).map((round, index) => ({
          roundNumber: Number(round.roundNumber) || index + 1,
          title: round.title || `Review ${index + 1}`,
          description: round.description || '',
          assignedUsers: round.assignedUsers || [],
        })));
      } catch (err) {
        console.error('Failed to load review rounds:', err);
      }
    };

    fetchProgramRounds();
  }, [token, selectedProgramId]);

  useEffect(() => {
    if (!token) return;

    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setUsers(data);
      } catch (err) {
        console.error('Failed to load users:', err);
      }
    };

    fetchUsers();
  }, [token]);

  useEffect(() => {
    const assignments = {};
    programRounds.forEach((round) => {
      assignments[round.roundNumber] = Array.isArray(round.assignedUsers)
        ? Array.from(new Set(round.assignedUsers.map((email) => getCanonicalEmail(email)).filter(Boolean)))
        : [];
    });
    setRoundAssignments(assignments);
  }, [programRounds]);

  const effectiveRounds = useMemo(() => {
    const baseRounds = programRounds.length > 0 ? programRounds : DEFAULT_REVIEW_ROUNDS;
    return baseRounds.map((round, index) => ({
      roundNumber: index + 1,
      title: round.title || `Review ${index + 1}`,
      description: round.description || '',
    }));
  }, [programRounds]);

  const updateRoundField = (index, field, value) => {
    setProgramRounds((current) => current.map((round, roundIndex) => (
      roundIndex === index
        ? { ...round, [field]: value }
        : round
    )));
  };

  const handleAddRound = () => {
    setProgramRounds((current) => ([
      ...current,
      {
        roundNumber: current.length + 1,
        title: `Review ${current.length + 1}`,
        description: '',
        assignedUsers: [],
      },
    ]));
  };

  const handleDeleteRound = (index) => {
    setProgramRounds((current) => current
      .filter((_, roundIndex) => roundIndex !== index)
      .map((round, roundIndex) => ({
        ...round,
        roundNumber: roundIndex + 1,
        title: round.title || `Review ${roundIndex + 1}`,
      })));
  };

  const handleSaveRounds = async () => {
    if (!selectedProgramId) return;

    const normalizedRounds = effectiveRounds.map((round, index) => ({
      roundNumber: index + 1,
      title: String(round.title || '').trim() || `Review ${index + 1}`,
      description: String(round.description || '').trim(),
      assignedUsers: roundAssignments[index + 1] || [],
    }));

    if (normalizedRounds.length === 0) {
      return;
    }

    try {
      setSaving(true);
      setFeedback('');

      const response = await fetch(`${API_BASE_URL}/api/programs/${selectedProgramId}/review-rounds`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reviewRoundTemplates: normalizedRounds }),
      });

      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.message || 'Failed to save review rounds.');
      }

      setProgramRounds((body.reviewRoundTemplates || []).map((round, index) => ({
        roundNumber: Number(round.roundNumber) || index + 1,
        title: round.title || `Review ${index + 1}`,
        description: round.description || '',
        assignedUsers: round.assignedUsers || [],
      })));
      setFeedback('Review rounds updated.');
      window.setTimeout(() => setFeedback(''), 2500);
    } catch (saveError) {
      console.error('Failed to save review rounds:', saveError);
    } finally {
      setSaving(false);
    }
  };

  const toggleAssignment = (roundNumber, email) => {
    setRoundAssignments((prev) => {
      const current = prev[roundNumber] || [];
      const next = current.includes(email)
        ? current.filter((item) => item !== email)
        : [...current, email];
      return { ...prev, [roundNumber]: next };
    });
  };

  const handleSaveAssignments = async (event) => {
    event.preventDefault();
    if (!token || !selectedProgramId) return;

    setSaving(true);
    setFeedback('');

    try {
      const reviewRoundTemplates = programRounds.map((round) => ({
        roundNumber: round.roundNumber,
        title: round.title,
        description: round.description || '',
        assignedUsers: roundAssignments[round.roundNumber] || [],
      }));

      const res = await fetch(`${API_BASE_URL}/api/programs/${selectedProgramId}/review-rounds`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reviewRoundTemplates }),
      });
      if (!res.ok) throw new Error(`Failed to save reviewer assignments (${res.status})`);
      const data = await res.json();
      setProgramRounds((data.reviewRoundTemplates || []).map((round, index) => ({
        roundNumber: Number(round.roundNumber) || index + 1,
        title: round.title || `Review ${index + 1}`,
        description: round.description || '',
        assignedUsers: round.assignedUsers || [],
      })));
      setFeedback('Reviewer assignments saved.');
      window.setTimeout(() => setFeedback(''), 2800);
    } catch (err) {
      console.error('Failed to save reviewer assignments:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="review-page">
      <section className="application-content">
        <div className="form-card">
          <h2 className="section-heading">Program Review Rounds</h2>
          <p className="form-description">
            Review rounds are managed at the program level and apply to every application in that program.
          </p>

          <label className="form-field">
            <span>Select Program</span>
            <select
              value={selectedProgramId}
              onChange={(event) => {
                setSelectedProgramId(event.target.value);
              }}
            >
              <option value="">Choose a program</option>
              {programs.map((program) => (
                <option key={program._id || program.id} value={program._id || program.id}>
                  {program.programCode} - {program.name}
                </option>
              ))}
            </select>
          </label>

          {feedback && <div className="success-message">{feedback}</div>}

          {selectedProgram && (
            <div className="workspace-review-rounds-editor">
              <div className="workspace-review-rounds-toolbar">
                <p className="table-meta">
                  Add, edit, or remove rounds for <strong>{selectedProgram.programCode}</strong>.
                </p>
                <div className="section-actions">
                  <button type="button" className="cancel-btn" onClick={handleAddRound}>
                    Add Round
                  </button>
                  <button type="button" className="submit-btn" onClick={handleSaveRounds} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Review Rounds'}
                  </button>
                </div>
              </div>

              <div className="review-history-table-wrapper">
                <table className="review-history-table">
                  <thead>
                    <tr>
                      <th>Round</th>
                      <th>Title</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {effectiveRounds.map((round, index) => (
                      <tr key={`${selectedProgramId}-${round.roundNumber}`}>
                        <td>{round.roundNumber}</td>
                        <td>
                          <input
                            className="search-input"
                            value={round.title}
                            onChange={(event) => updateRoundField(index, 'title', event.target.value)}
                            placeholder={`Review ${index + 1}`}
                          />
                        </td>
                        <td>
                          <input
                            className="search-input"
                            value={round.description}
                            onChange={(event) => updateRoundField(index, 'description', event.target.value)}
                            placeholder={index === 0 ? 'ETA Internal Reviewers' : 'Add round description'}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="link-btn"
                            onClick={() => handleDeleteRound(index)}
                            disabled={effectiveRounds.length <= 1}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="form-card">
          <h2 className="section-heading">Assign Reviewers to Rounds</h2>
          <p className="form-description">
            Select the reviewers responsible for each round. Assignments apply to every application in the selected program.
          </p>

          {!selectedProgramId ? (
            <p className="no-results">Select a program to assign reviewers to its review rounds.</p>
          ) : (
            <form onSubmit={handleSaveAssignments} noValidate>
              {programRounds.map((round) => (
                <div className="form-field" key={`assign-${round.roundNumber}`}>
                  <span>{`Round ${round.roundNumber} - ${round.title}`}</span>
                  <div className="user-role-grid">
                    {users.map((user) => (
                      <label key={`assign-${round.roundNumber}-${user.id}`} className="compact-checkbox">
                        <input
                          type="checkbox"
                          checked={(roundAssignments[round.roundNumber] || []).includes(user.email)}
                          onChange={() => toggleAssignment(round.roundNumber, user.email)}
                        />
                        <span>{getDisplayName(user)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              {users.length === 0 && (
                <p className="no-results">No users are available to assign.</p>
              )}

              <div className="form-actions">
                <button type="submit" className="submit-btn" disabled={saving || users.length === 0}>
                  {saving ? 'Saving…' : 'Save Assignments'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReviewRounds;
